import React from 'react';

/**
 * Beautiful Custom Dialog Component
 * Replaces ugly browser alerts and confirms
 */
const CustomDialog = ({ 
  show, 
  type = 'confirm', // 'confirm', 'alert', 'success', 'error', 'warning'
  title, 
  message, 
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm, 
  onCancel,
  icon
}) => {
  if (!show) return null;

  // Icon and color based on type
  const getTypeConfig = () => {
    switch(type) {
      case 'success':
        return {
          icon: icon || '✅',
          gradient: 'linear-gradient(135deg, #10b981, #059669)',
          iconBg: 'linear-gradient(135deg, #d1fae5, #a7f3d0)'
        };
      case 'error':
      case 'delete':
        return {
          icon: icon || '⚠️',
          gradient: 'linear-gradient(135deg, #ef4444, #dc2626)',
          iconBg: 'linear-gradient(135deg, #fee2e2, #fecaca)'
        };
      case 'warning':
        return {
          icon: icon || '⚠️',
          gradient: 'linear-gradient(135deg, #f59e0b, #d97706)',
          iconBg: 'linear-gradient(135deg, #fef3c7, #fde68a)'
        };
      case 'logout':
        return {
          icon: icon || '👋',
          gradient: 'linear-gradient(135deg, #667eea, #764ba2)',
          iconBg: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)'
        };
      default:
        return {
          icon: icon || '❓',
          gradient: 'linear-gradient(135deg, #667eea, #764ba2)',
          iconBg: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)'
        };
    }
  };

  const config = getTypeConfig();

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      padding: '20px',
      backdropFilter: 'blur(4px)',
      animation: 'fadeIn 0.2s ease-out'
    }}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes scaleIn {
            from { transform: scale(0.9); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
        `}
      </style>
      
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: '40px',
        maxWidth: '480px',
        width: '100%',
        boxShadow: '0 25px 80px rgba(0,0,0,0.3)',
        animation: 'scaleIn 0.3s ease-out',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Gradient accent bar */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '6px',
          background: config.gradient
        }} />
        
        {/* Icon */}
        <div style={{
          width: '96px',
          height: '96px',
          margin: '0 auto 24px',
          background: config.iconBg,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '48px',
          animation: 'pulse 2s infinite'
        }}>
          {config.icon}
        </div>
        
        {/* Title */}
        <h3 style={{
          fontSize: '26px',
          fontWeight: 'bold',
          color: '#1f2937',
          marginBottom: '16px',
          textAlign: 'center',
          lineHeight: '1.3'
        }}>
          {title}
        </h3>
        
        {/* Message */}
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          textAlign: 'center',
          marginBottom: '36px',
          lineHeight: '1.6'
        }}>
          {message}
        </p>
        
        {/* Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '14px',
          flexDirection: type === 'alert' ? 'column' : 'row'
        }}>
          {type !== 'alert' && onCancel && (
            <button
              onClick={onCancel}
              style={{
                flex: 1,
                padding: '16px',
                border: '2px solid #e5e7eb',
                background: 'white',
                color: '#4b5563',
                borderRadius: '14px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#f9fafb';
                e.target.style.borderColor = '#d1d5db';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'white';
                e.target.style.borderColor = '#e5e7eb';
              }}
            >
              {cancelText}
            </button>
          )}
          
          <button
            onClick={onConfirm || onCancel}
            style={{
              flex: 1,
              padding: '16px',
              border: 'none',
              background: config.gradient,
              color: 'white',
              borderRadius: '14px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 4px 20px rgba(102, 126, 234, 0.3)'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 8px 30px rgba(102, 126, 234, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.3)';
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomDialog;
